'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import SignupHeader from '@/components/SignupHeader';
import Toast from '@/components/Toast';
import '@/styles/global.css';

export default function ForgotPasswordPage() {
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setToast({ message: 'Please enter your email address', type: 'error' });
      return;
    }

    if (!validateEmail(email)) {
      setToast({ message: 'Please enter a valid email address', type: 'error' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        setToast({ message: data.error || 'Email not found', type: 'error' });
        setIsLoading(false);
        return;
      }

      setToast({ message: 'Email verified! Please set your new password.', type: 'success' });
      setTimeout(() => {
        setStep('reset');
      }, 1500);
      
    } catch (error) {
      console.error('Email verification error:', error);
      setToast({ message: 'Failed to verify email. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setToast({ message: 'Please fill in all fields', type: 'error' });
      return;
    }

    if (newPassword.length < 8) {
      setToast({ message: 'Password must be at least 8 characters', type: 'error' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setToast({ message: 'Passwords do not match', type: 'error' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        setToast({ message: data.error || 'Failed to reset password', type: 'error' });
        setIsLoading(false);
        return;
      }

      setToast({ message: 'Password reset successful! Redirecting to login...', type: 'success' });
      
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      
    } catch (error) {
      console.error('Password reset error:', error);
      setToast({ message: 'Failed to reset password. Please try again.', type: 'error' });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <SignupHeader />

      <section className="join-section py-20">
        <div className="max-w-[1380px] mx-auto px-[20px]">
          <div className="grid md:grid-cols-2 gap-30">
            {/* Left Side */}
            <div className="join-left">
              <p className="eyebrow font-['Archivo',sans-serif] text-[16px] text-[#8A7296] mb-4">
                • Password Recovery
              </p>
              <h1 className="font-['Gotham_Rounded',sans-serif] text-[48px] font-bold text-[#2E2A3A] leading-[56px] mb-6">
                {step === 'email' ? 'Forgot your password?' : 'Set new password'}
              </h1>
              <p className="desc font-['Archivo',sans-serif] text-[18px] text-[#6e6672] leading-[28px]">
                {step === 'email' 
                  ? 'No worries! Enter your email address and we\'ll help you reset your password.'
                  : 'Enter your new password below. Make sure it\'s at least 8 characters long.'
                }
              </p>
            </div>

            {/* Right Side - Form */}
            <div>
              {step === 'email' ? (
                <form className="signin-card" onSubmit={handleEmailSubmit}>
                  <h2>Verify Email</h2>

                  <label>Email Address</label>
                  <input 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="e.g. you@example.com"
                    disabled={isLoading}
                  />

                  <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Verifying...' : 'Verify Email'}
                    {!isLoading && <Image src="/btn-arrow.svg" width={16} height={16} alt="arrow" className="inline-block ml-2" />}
                  </button>

                  <div className="text-center mt-6">
                    <Link 
                      href="/login" 
                      className="font-['Archivo',sans-serif] text-[14px] text-[#8A7296] hover:text-[#7a647d] transition-colors font-medium"
                    >
                      ← Back to Login
                    </Link>
                  </div>
                </form>
              ) : (
                <form className="signin-card" onSubmit={handlePasswordReset}>
                  <h2>Reset Password</h2>

                  <label>Email</label>
                  <input 
                    type="email" 
                    value={email}
                    disabled
                    className="bg-gray-100"
                  />

                  <label>New Password</label>
                  <input 
                    type="password" 
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    disabled={isLoading}
                  />

                  <label>Confirm New Password</label>
                  <input 
                    type="password" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    disabled={isLoading}
                  />

                  <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                    {!isLoading && <Image src="/btn-arrow.svg" width={16} height={16} alt="arrow" className="inline-block ml-2" />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={3000}
        />
      )}
    </div>
  );
}
